import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Check, Mail, ArrowRight, Loader2, Copy, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orders/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => setOrder(null)).finally(() => setLoading(false));
  }, [orderNumber]);

  const copyEmail = async () => {
    try { await navigator.clipboard.writeText("designmystoryus@gmail.com"); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  return (
    <div className="min-h-screen bg-[color:var(--dms-cream)]">
      <Navbar />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-16">
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--dms-peach)] mx-auto" /></div>
        ) : !order ? (
          <div className="py-20 text-center">
            <h1 className="font-serif text-3xl text-[color:var(--dms-navy)]">Order not found</h1>
            <Link to="/" className="link-peach mt-6 inline-flex">Back to home</Link>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 md:p-12">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center">
                <Check className="w-8 h-8 text-[color:var(--dms-navy)]" />
              </div>
              <p className="kicker mt-6">{order.status === "awaiting-payment" ? "Order placed · Awaiting payment" : "Order confirmed"}</p>
              <h1 className="font-serif text-4xl md:text-5xl text-[color:var(--dms-navy)] mt-3">Thank you, {order.customer_name?.split(" ")[0]}!</h1>
              <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
              <p className="mt-6 text-[color:var(--dms-navy)]/70">We emailed your invoice to <span className="font-semibold text-[color:var(--dms-navy)]">{order.customer_email}</span>.</p>
            </div>

            {order.status === "awaiting-payment" && (
              <div className="mt-8 bg-[color:var(--dms-cream-2)]/60 border border-dashed border-[color:var(--dms-peach)] rounded-2xl p-6" data-testid="order-zelle-box">
                <p className="kicker">Pay by Zelle to confirm your order</p>
                <div className="mt-4 space-y-3">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <span className="text-xs text-[color:var(--dms-navy)]/60 tracking-widest uppercase font-semibold">Amount due</span>
                    <span className="font-serif text-3xl text-[color:var(--dms-navy)]">${order.total.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <span className="text-xs text-[color:var(--dms-navy)]/60 tracking-widest uppercase font-semibold">Send to</span>
                    <button onClick={copyEmail} className="font-mono text-base text-[color:var(--dms-navy)] inline-flex items-center gap-2 hover:text-[color:var(--dms-peach)]" data-testid="order-copy-zelle">
                      designmystoryus@gmail.com
                      {copied ? <CheckCircle2 className="w-4 h-4 text-[color:var(--dms-peach)]" /> : <Copy className="w-4 h-4 opacity-50" />}
                    </button>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <span className="text-xs text-[color:var(--dms-navy)]/60 tracking-widest uppercase font-semibold">Memo</span>
                    <span className="font-mono text-base text-[color:var(--dms-navy)]"><b>#{order.order_number}</b></span>
                  </div>
                </div>
                <p className="mt-5 text-xs text-[color:var(--dms-navy)]/60">Once your Zelle payment arrives, we'll email you a confirmation and start printing.</p>
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 gap-4">
              <Info label="Order number" value={`#${order.order_number}`} />
              <Info label="Total" value={`$${order.total.toFixed(2)}`} />
            </div>

            <div className="mt-8">
              <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Items</p>
              <ul className="mt-3 divide-y divide-[color:var(--dms-navy)]/10">
                {order.items.map((it, i) => (
                  <li key={i} className="py-3 flex items-center gap-4">
                    {it.img && <div className="w-14 h-14 rounded-lg overflow-hidden bg-[color:var(--dms-peach-light)]/40 flex-shrink-0"><img src={it.img} alt="" className="w-full h-full object-cover" /></div>}
                    <div className="flex-1">
                      <p className="font-serif text-[color:var(--dms-navy)]">{it.title}</p>
                      <p className="text-xs text-[color:var(--dms-navy)]/60 mt-1">{it.color_name} · {it.size} · x{it.qty}</p>
                    </div>
                    <p className="font-serif text-[color:var(--dms-navy)]">${(it.price * it.qty).toFixed(2)}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 bg-[color:var(--dms-cream-2)] rounded-xl p-5 flex items-start gap-3">
              <Mail className="w-5 h-5 text-[color:var(--dms-peach)] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[color:var(--dms-navy)]/80">
                <p className="font-semibold text-[color:var(--dms-navy)]">What happens next</p>
                <p className="mt-1">We'll review your order, prep a proof if it includes custom work, and email you before printing anything. Reply to your confirmation email with any questions.</p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link to="/" className="btn-navy">Back to shop <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-[color:var(--dms-cream-2)] rounded-xl p-4">
      <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}</p>
      <p className="font-serif text-xl text-[color:var(--dms-navy)] mt-1">{value}</p>
    </div>
  );
}
